import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface MotiocompAnalyticsSummary {
  totalVisitors: number
  totalVisitorsAll: number
  landingVisitors: number
  landingVisitorsAll: number
  /** Distinct IPs for motiocomp_account_registered in range */
  accountsRegistered: number
  /** Total rows for motiocomp_account_registered in range */
  accountsRegisteredAll: number
  ticketsCompiled: number
  ticketsCompiledAll: number
  outputChecksRun: number
  outputChecksRunAll: number
  avgDurationSeconds: number | null
  totalActivityEvents: number
  avgActionsPerSession: number
}

export interface DailyTrendRow {
  date: string
  visitor_count: number
}

export interface DeviceRow {
  device: string
  count: number
}

export interface LocationRow {
  country: string
  count: number
}

export interface CityRow {
  city: string
  region: string
  country: string
  count: number
}

export interface RegionRow {
  region: string
  country: string
  count: number
}

export interface SourceRow {
  source: string
  count: number
}

export interface MotiocompKpiBreakdownRow {
  key: string
  label: string
  total: number
  unique: number
}

export interface MotiocompActionStatRow {
  action: string
  label: string
  total_occurrences: number
  unique_users: number
}

export interface MotiocompStepRow {
  action: string
  label: string
  session_count: number
}

export interface MotiocompClientErrorRow {
  id: string
  kind: 'logged_in' | 'landing'
  created_at: string
  user_id: number | null
  user_email: string | null
  message: string
  details: string | null
  where_section?: string | null
  ip_address: string | null
}

export interface MotiocompAnalyticsData {
  dataRange: { firstDate: string | null; lastDate: string | null }
  summary: MotiocompAnalyticsSummary
  dailyTrend: DailyTrendRow[]
  dailyTrendAll: DailyTrendRow[]
  landingDailyTrend: DailyTrendRow[]
  landingDailyTrendAll: DailyTrendRow[]
  deviceBreakdown: DeviceRow[]
  locationBreakdown: LocationRow[]
  cityBreakdown: CityRow[]
  regionBreakdown: RegionRow[]
  sourceBreakdown: SourceRow[]
  compileTargets: MotiocompKpiBreakdownRow[]
  outputCheckTargets: MotiocompKpiBreakdownRow[]
  actionStats: MotiocompActionStatRow[]
  firstSteps: MotiocompStepRow[]
  lastSteps: MotiocompStepRow[]
  clientErrors: MotiocompClientErrorRow[]
}

export const motiocompAnalyticsApi = createApi({
  reducerPath: 'motiocompAnalyticsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['MotiocompAnalytics'],
  endpoints: (builder) => ({
    getMotiocompAnalytics: builder.query<
      { data: MotiocompAnalyticsData },
      { dateFrom?: string; dateTo?: string }
    >({
      query: (params) => ({
        url: '/motiocomp-analytics',
        params,
      }),
      providesTags: ['MotiocompAnalytics'],
    }),
    deleteMotiocompClientErrors: builder.mutation<
      { success: boolean; deleted: number },
      { ids: string[] }
    >({
      query: (body) => ({
        url: '/motiocomp-analytics/client-errors/delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MotiocompAnalytics'],
    }),
  }),
})

export const {
  useGetMotiocompAnalyticsQuery,
  useDeleteMotiocompClientErrorsMutation,
} = motiocompAnalyticsApi
